import { Box, DialogContent, Stack } from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DataTable } from '../../../../common/table/DataTable';
import { Button, Checkbox, colors, Typography } from '@sweep-io/sweep-design';
import { DataTableBaseColumn } from '../../../../common/table/TableTypes';
import { NewMemberStatusDialog } from './NewMemberStatusDialog';
import { Loader } from '../../../../common/Loader';
import { CampaignMemberStatusContext } from '../../campaign-member-status/CampaignMemberStatusContext';
import { useEffectFetchCampaignMemberStatuses } from '../../campaign-member-status/useEffectFetchCampaignMemberStatuses';
import { useCampaignMemberStatuses } from '../../campaign-member-status/useCampaignMemberStatuses';
import usePermission from '../../../../common/permissions/usePermission';
import RestrictedTooltip from '../../../../common/permissions/RestrictedTooltip';
import { DataTableId } from '../../../../common/table/utils';
import { ActionsMenu } from '../../../../common/actions-menu/ActionsMenu';
import { ReplaceStatusDialog } from './ReplaceStatusDialog';
import { useFeatureToggle } from '../../../../../feature-flags/useFeatureToggle';
const CREATE_MEMBER_STATUS_PERMISSION = 'create:campaigns';

export const LoadedStatusesTab = ({ campaignId }: { campaignId: string }) => {
  const { campaignMemberStatuses } = useContext(CampaignMemberStatusContext);
  const [isCreateMemberStatusAllowed] = usePermission([CREATE_MEMBER_STATUS_PERMISSION]); // TODO: Fix with the correct permission

  const [newMemberDialogOpen, setNewMemberDialogOpen] = useState(false);
  const { updateCampaignMemberStatus } = useCampaignMemberStatuses();

  const checkboxRenderer = useCallback(
    (value: boolean, id: string, fieldName: string) => (
      <Checkbox
        checked={value}
        disabled={!isCreateMemberStatusAllowed}
        onChange={async (e) => {
          const exitingStatus = campaignMemberStatuses.find((status) => status.Id === id);
          if (!exitingStatus) {
            return;
          }
          if (fieldName === 'IsDefault' && exitingStatus.IsDefault) {
            return; // Can't uncheck default
          }
          const newStatus = { ...exitingStatus, [fieldName]: e.target.checked };
          await updateCampaignMemberStatus(newStatus);
        }}
      />
    ),
    [campaignMemberStatuses, isCreateMemberStatusAllowed, updateCampaignMemberStatus],
  );

  const [replaceDialogOpen, setReplaceDialogOpen] = useState<string | null>(null);

  const { hubspotV2_5 } = useFeatureToggle();

  const columns: DataTableBaseColumn[] = useMemo(() => {
    const columns: DataTableBaseColumn[] = [
      { headerName: 'Value', field: 'Label' },
      {
        headerName: 'Default',
        field: 'IsDefault',
        renderer: checkboxRenderer,
      },
      {
        headerName: 'Responded',
        field: 'HasResponded',
        renderer: checkboxRenderer,
      },
    ];
    if (hubspotV2_5) {
      columns.push({
        headerName: '',
        field: 'actions',
        justifyContent: 'right',
        width: 'auto',
        renderer: (value: boolean, id: string) => {
          if (campaignMemberStatuses.length === 1) {
            return null;
          }
          return (
            <Box display="flex" justifyContent="right" marginRight={2}>
              <ActionsMenu
                actions={[
                  {
                    label: 'Delete',
                    value: 'delete',
                  },
                ]}
                onClick={() => {
                  setReplaceDialogOpen(id);
                }}
                iconVariant="system"
              />
            </Box>
          );
        },
      });
    }
    return columns;
  }, [checkboxRenderer, campaignMemberStatuses, hubspotV2_5]);

  const rows = campaignMemberStatuses.map((status) => ({
    id: status.Id,
    ...status,
  }));

  const maybeRenderNewMemberStatusDialog = () => {
    if (!newMemberDialogOpen) {
      return null;
    }
    return (
      <NewMemberStatusDialog
        onClose={() => setNewMemberDialogOpen(false)}
        campaignId={campaignId}
      />
    );
  };

  const maybeRenderReplaceStatusDialog = () => {
    if (!replaceDialogOpen) {
      return null;
    }
    return (
      <ReplaceStatusDialog
        statusId={replaceDialogOpen}
        onClose={() => setReplaceDialogOpen(null)}
        campaignId={campaignId}
      />
    );
  };

  return (
    <>
      <DialogContent sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body" color={colors.grey[700]}>
            {campaignMemberStatuses.length} member statuses
          </Typography>
          <RestrictedTooltip
            to={[CREATE_MEMBER_STATUS_PERMISSION]}
            notAllowedTitle={'To create campaign member status please contact your admin'}
          >
            <Button
              size="small"
              onClick={() => setNewMemberDialogOpen(true)}
              disabled={!isCreateMemberStatusAllowed}
            >
              New Status
            </Button>
          </RestrictedTooltip>
        </Box>
        <Stack height="calc(100% - 60px)" overflow="auto">
          <DataTable
            columns={columns}
            rows={rows}
            stickyHeader
            tableId={DataTableId.HubspotStatusesTable}
          />
        </Stack>
      </DialogContent>
      {maybeRenderNewMemberStatusDialog()}
      {maybeRenderReplaceStatusDialog()}
    </>
  );
};

export const StatusesTab = ({ campaignId }: { campaignId: string }) => {
  const { loadStatus } = useEffectFetchCampaignMemberStatuses(campaignId);

  if (loadStatus !== 'success') {
    return (
      <DialogContent>
        <Loader />
      </DialogContent>
    );
  }

  return <LoadedStatusesTab campaignId={campaignId} />;
};
